/**
 * The order as it fills in: one plain object, the ten must-haves, and the conversions to what the API takes.
 * Everything here is pure so the page, Lykke's chat and Lykke's voice tools all write through the same rules.
 */
import type { OrderBody, OrderIdea, OrderPrepare, OrderSet } from './types';

export interface OrderState {
  productId: string | null;
  /** The product in the client's words, when the Brand OS has none that fits. */
  productName: string;
  goal: string | null;
  market: string;
  currency: string;
  budget: number | null;
  start: string;
  audienceIds: string[];
  /** Who to reach in the client's words, when the Brand OS has no audience that fits. */
  audienceText: string;
  idea: OrderIdea | null;
  delivery: string | null;
  approverName: string;
  approverEmail: string;
  service: 'full' | 'autonomous' | null;
  terms: boolean;
  notes: string[];
  /** Creator lists the client points at; scouted = made for this order, Rex fills it. Optional. */
  lists: Array<{ id: string; title: string; scouted?: boolean }>;
}

export const CHAPTERS = ['Brand and goal', 'Audience and idea', 'Creators and plan', 'Order'] as const;
export type Chapter = 0 | 1 | 2 | 3;

export interface MustHave { id: string; label: string; chapter: Chapter; done: (s: OrderState) => boolean }
export const MUST_HAVES: MustHave[] = [
  { id: 'product', label: 'Product', chapter: 0, done: (s) => !!s.productId || s.productName.trim().length > 1 },
  { id: 'goal', label: 'Goal', chapter: 0, done: (s) => !!s.goal },
  { id: 'market', label: 'Market', chapter: 0, done: (s) => !!s.market },
  { id: 'budget', label: 'Budget', chapter: 0, done: (s) => (s.budget ?? 0) > 0 },
  { id: 'start', label: 'Start', chapter: 0, done: (s) => /^\d{4}-\d{2}-\d{2}$/.test(s.start) },
  { id: 'audience', label: 'Audience', chapter: 1, done: (s) => s.audienceIds.length > 0 || s.audienceText.trim().length > 2 },
  { id: 'idea', label: 'Idea', chapter: 1, done: (s) => !!s.idea },
  { id: 'delivery', label: 'Product delivery', chapter: 2, done: (s) => !!s.delivery },
  { id: 'approver', label: 'Approver', chapter: 2, done: (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.approverEmail) },
  { id: 'terms', label: 'Service and terms', chapter: 3, done: (s) => !!s.service && s.terms },
];

export const doneCount = (s: OrderState) => MUST_HAVES.filter((m) => m.done(s)).length;
export const missing = (s: OrderState) => MUST_HAVES.filter((m) => !m.done(s));
/** A chapter may be left once its own must-haves are in. */
export const chapterReady = (s: OrderState, ch: Chapter) => MUST_HAVES.filter((m) => m.chapter === ch).every((m) => m.done(s));

/** The first state, from the Brand OS and the brand's last campaign. Nothing is chosen for the user that they must decide. */
export function initialState(p: OrderPrepare): OrderState {
  const product = p.products[0] || null;
  return {
    productId: product?.id ?? null,
    productName: '',
    goal: null,
    market: p.defaults.market,
    currency: p.defaults.currency || 'SEK',
    budget: p.defaults.budget || null,
    start: p.defaults.start,
    audienceIds: product ? audiencesFor(p, product.id) : [],
    audienceText: '',
    idea: null,
    delivery: null,
    approverName: p.me.name,
    approverEmail: p.me.email,
    service: null,
    terms: false,
    notes: [],
    lists: [],
  };
}

/** The Brand OS audiences linked to a product, or the first one when none is linked. */
export function audiencesFor(p: OrderPrepare, productId: string): string[] {
  const linked = p.audiences.filter((a) => a.productIds.includes(productId)).map((a) => a.id);
  return linked.length ? linked.slice(0, 3) : p.audiences.slice(0, 1).map((a) => a.id);
}

/** One receipt per field in a row: a new answer to the field just written replaces that receipt instead of stacking. */
export function addWrote<T extends { kind: string; field?: string }>(t: T[], w: T & { kind: 'wrote'; field: string }): T[] {
  const last = t[t.length - 1];
  if (last && last.kind === 'wrote' && last.field === w.field) return [...t.slice(0, -1), w];
  return [...t, w];
}

/** The product's name, from the Brand OS or as the client wrote it. */
export const productLabel = (s: OrderState, p: OrderPrepare): string => p.products.find((x) => x.id === s.productId)?.name || s.productName.trim();

/** A name the client said matched against a list shown on screen ("the barrier cream" finds "Daily Barrier Cream"). */
export function matchByName<T extends { name: string }>(xs: T[], said: string): T | undefined {
  const q = said.trim().toLowerCase(); if (q.length < 2) return undefined;
  return xs.find((x) => x.name.toLowerCase() === q) || xs.find((x) => x.name.toLowerCase().includes(q) || q.includes(x.name.toLowerCase()));
}

/** Apply what Lykke heard (chat or voice). Only known values; the caller has already checked the user said them. */
export function applySet(s: OrderState, set: OrderSet, p: OrderPrepare): { next: OrderState; wrote: Array<{ field: string; value: string; chapter: Chapter }> } {
  const next = { ...s }; const wrote: Array<{ field: string; value: string; chapter: Chapter }> = [];
  const product = String(set.product || '').trim().slice(0, 160);
  if (product.length > 1) {
    const pr = matchByName(p.products, product);
    if (pr) { next.productId = pr.id; next.productName = ''; next.audienceIds = audiencesFor(p, pr.id); next.idea = null; wrote.push({ field: 'Product', value: pr.name, chapter: 0 }); }
    else { next.productId = null; next.productName = product; next.idea = null; wrote.push({ field: 'Product', value: product, chapter: 0 }); }
  }
  const audience = String(set.audience || '').trim().slice(0, 300);
  if (audience.length > 2) {
    const a = matchByName(p.audiences, audience);
    if (a) { if (!next.audienceIds.includes(a.id)) next.audienceIds = [...next.audienceIds, a.id]; wrote.push({ field: 'Audience', value: a.name, chapter: 1 }); }
    else { next.audienceText = audience; wrote.push({ field: 'Audience', value: audience.length > 48 ? `${audience.slice(0, 48)}…` : audience, chapter: 1 }); }
  }
  if (set.goal && p.goals.some((g) => g.id === set.goal)) { next.goal = set.goal; wrote.push({ field: 'Goal', value: p.goals.find((g) => g.id === set.goal)!.title, chapter: 0 }); }
  if (set.market && p.markets.some((m) => m.id === set.market)) { next.market = set.market; wrote.push({ field: 'Market', value: p.markets.find((m) => m.id === set.market)!.name, chapter: 0 }); }
  if (set.budget && set.budget > 0) { next.budget = Math.round(set.budget); wrote.push({ field: 'Budget', value: `${formatNumber(next.budget)} ${s.currency}`, chapter: 0 }); }
  if (set.start && /^\d{4}-\d{2}-\d{2}$/.test(set.start) && set.start >= p.defaults.earliestStart) { next.start = set.start; wrote.push({ field: 'Start', value: formatDate(set.start), chapter: 0 }); }
  if (set.delivery && p.delivery.some((d) => d.id === set.delivery)) { next.delivery = set.delivery; wrote.push({ field: 'Product delivery', value: p.delivery.find((d) => d.id === set.delivery)!.label, chapter: 2 }); }
  if (set.approverEmail) { next.approverEmail = set.approverEmail; wrote.push({ field: 'Approver', value: set.approverEmail, chapter: 2 }); }
  if (set.note) { next.notes = [...s.notes, set.note]; wrote.push({ field: 'Note for the brief', value: set.note.length > 48 ? `${set.note.slice(0, 48)}…` : set.note, chapter: 1 }); }
  return { next, wrote };
}

/** What the order endpoint takes. */
export function toOrderBody(s: OrderState, p: OrderPrepare): OrderBody {
  const product = p.products.find((x) => x.id === s.productId);
  const auds = p.audiences.filter((a) => s.audienceIds.includes(a.id));
  const ownWords = s.audienceText.trim();
  const ages = auds.flatMap((a) => [a.ageMin, a.ageMax]).filter((n): n is number => typeof n === 'number');
  const genders = [...new Set(auds.map((a) => a.gender))];
  return {
    brandId: p.brand.id, brandName: p.brand.name, productId: product?.id, productName: productLabel(s, p) || undefined, productDescription: product?.description,
    goal: s.goal || '', market: s.market, currency: s.currency, budget: s.budget || 0, start: s.start,
    audienceIds: s.audienceIds, audienceText: [...auds.map((a) => `${a.name}${a.ageRange ? ` (${a.ageRange})` : ''}: ${a.description}`), ...(ownWords ? [ownWords] : [])].join('\n'),
    ageMin: ages.length ? Math.min(...ages) : undefined, ageMax: ages.length ? Math.max(...ages) : undefined,
    gender: genders.length === 1 ? genders[0] : 'Any',
    idea: s.idea ? { title: s.idea.title, hook: s.idea.hook, description: s.idea.description, keyMessage: s.idea.keyMessage } : undefined,
    delivery: s.delivery || undefined, approver: { name: s.approverName, email: s.approverEmail },
    service: s.service || undefined, notes: s.notes, termsAccepted: s.terms, lists: s.lists,
  };
}

/** A compact view of the order for Lykke's context. */
export function stateForLykke(s: OrderState, p: OrderPrepare) {
  return {
    product: productLabel(s, p) || null,
    brandOsProducts: p.products.slice(0, 12).map((x) => x.name),
    brandOsAudiences: p.audiences.slice(0, 8).map((a) => a.name),
    goal: p.goals.find((g) => g.id === s.goal)?.title || null,
    market: p.markets.find((m) => m.id === s.market)?.name || s.market,
    budget: s.budget ? `${s.budget} ${s.currency}` : null, start: s.start,
    audiences: [...p.audiences.filter((a) => s.audienceIds.includes(a.id)).map((a) => a.name), ...(s.audienceText.trim() ? [s.audienceText.trim()] : [])],
    idea: s.idea?.title || null, delivery: s.delivery, approver: s.approverEmail || null,
    service: s.service, termsAccepted: s.terms, creatorLists: s.lists.map((l) => l.title), missing: missing(s).map((m) => m.label),
  };
}

/** "1k to 10k and 10k to 50k" as a follower range for a search. */
export function followerRange(band: string | undefined): { min: number; max: number } {
  const n = [...String(band || '').matchAll(/(\d+)\s*k/gi)].map((m) => Number(m[1]) * 1000);
  return n.length >= 2 ? { min: Math.max(1000, Math.min(...n)), max: Math.max(...n) } : { min: 10000, max: 50000 };
}

export const formatNumber = (n: number) => Math.round(n).toLocaleString('sv-SE').replace(/\u00a0/g, ' ');
export const formatDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
export const shortNumber = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(Math.round(n)));
